/**
 * The ONLY place that reads environment config. Base URL and the three group
 * canonicals come from env (build-spec §2), never hard-coded in components.
 * This keeps the backend swappable (Xano → FastAPI) behind the api/ seam.
 */

type ApiGroup = "auth" | "public" | "staff" | "leads" | "jobs" | "tasks" | "apps" | "inv" | "letters" | "dashboard" | "quote" | "planwire" | "buildcost" | "feefo";

function required(value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(
      `Missing required env var ${name}. Copy .env.example → .env.local and fill it in.`,
    );
  }
  return value.replace(/\/+$/, ""); // strip any trailing slash
}

/** Env override with a fallback, so a new group canonical doesn't crash an
 *  existing deploy that hasn't had the var added yet. */
function withDefault(value: string | undefined, fallback: string): string {
  return (value || fallback).replace(/\/+$/, "");
}

const base = required(import.meta.env.VITE_API_BASE, "VITE_API_BASE");

const canonicals: Record<ApiGroup, string> = {
  auth: required(import.meta.env.VITE_API_CANONICAL_AUTH, "VITE_API_CANONICAL_AUTH"),
  public: required(import.meta.env.VITE_API_CANONICAL_PUBLIC, "VITE_API_CANONICAL_PUBLIC"),
  staff: required(import.meta.env.VITE_API_CANONICAL_STAFF, "VITE_API_CANONICAL_STAFF"),
  // LeadsStaff group; defaults to the documented canonical when unset.
  leads: withDefault(import.meta.env.VITE_API_CANONICAL_LEADS, "a3FSi1Fj"),
  // Jobs hub + Tasks groups (Jobs-Hub Brief 01); same Xano base, defaulted to
  // the documented canonicals so a deploy without the new vars still works.
  jobs: withDefault(import.meta.env.VITE_API_CANONICAL_JOBS, "H6IHDfRP"),
  tasks: withDefault(import.meta.env.VITE_API_CANONICAL_TASKS, "LaJMLlbG"),
  // Applications group (Jobs-Hub Brief 03); same Xano base, defaulted canonical.
  apps: withDefault(import.meta.env.VITE_API_CANONICAL_APPS, "CI935tE1"),
  // Invoicing group (Jobs-Hub Brief 04 — fees; Brief 05 — invoices).
  inv: withDefault(import.meta.env.VITE_API_CANONICAL_INVOICING, "M8XIPbVz"),
  // Letters group (Jobs-Hub Brief 06 — read-only letter detail).
  letters: withDefault(import.meta.env.VITE_API_CANONICAL_LETTERS, "S2Lb2uzv"),
  // Studio overview dashboard group (single /dashboard/summary endpoint).
  dashboard: withDefault(import.meta.env.VITE_API_CANONICAL_DASHBOARD, "x_xwoe-E"),
  // Quote group (Accepted Quote Workbench — accept + provision-plan + provision).
  quote: withDefault(import.meta.env.VITE_API_CANONICAL_QUOTE, "EBXrikCU"),
  // PlanWire group (read-only live planning-status lookup on an application).
  planwire: withDefault(import.meta.env.VITE_API_CANONICAL_PLANWIRE, "42dmnPbY"),
  // Build-cost estimator group (deterministic cost calc + job artifact store). Public.
  buildcost: withDefault(import.meta.env.VITE_API_CANONICAL_BUILDCOST, "-2oyGJV8"),
  // Feefo review-request lifecycle group (send + track + sync). Staff endpoints
  // admin-gated; the webhook is public. Defaulted to the documented canonical so
  // a deploy without the new var still works.
  feefo: withDefault(import.meta.env.VITE_API_CANONICAL_FEEFO, "mum9SwXP"),
};

export const config = {
  base,
  canonicals,
  /** Modal feasibility engine base (spec §6). Defaults to the live deployment
   *  when unset so an existing deploy keeps working without a new env var. */
  modalFeasibilityBase: withDefault(
    import.meta.env.VITE_MODAL_FEASIBILITY_BASE,
    "https://duncan-65415--arkitectly-feasibility-web.modal.run",
  ),
  /** Ideal Postcodes API key (spec §6 — address/council/uprn/lat/lng lookup).
   *  Optional: when absent, the report form's fields stay manually editable. */
  idealPostcodesKey: import.meta.env.VITE_IDEAL_POSTCODES_KEY ?? "",
  /** VAPID public key for staff Web Push — pairs with the arkitectly-push
   *  Worker's private key (apps/push-worker). Public by nature, so it code-
   *  defaults like the canonicals; changing it orphans every existing
   *  subscription, so treat a rotation as a coordinated redeploy. */
  vapidPublicKey:
    import.meta.env.VITE_VAPID_PUBLIC_KEY ??
    "BEfibKEqLPBjqXS4FfUerlrJmwQ2oVi4Hf14UD3HCbdi-aEco87zbMJPJVw51gjo-dp91iLh9884_50aVujtue0",
} as const;

/** Build a full endpoint URL: `{base}/api:{canonical}/{path}`. */
export function endpoint(group: ApiGroup, path: string): string {
  const clean = path.replace(/^\/+/, "");
  return `${config.base}/api:${config.canonicals[group]}/${clean}`;
}

export type { ApiGroup };
